import { Cookie, Settings, MonitorSmartphone, Target, RefreshCw, HelpCircle, Info, Shield } from "lucide-react";
import "./CookiesPolicy.css";

const sections = [
  { id: "what", label: "What are cookies?" },
  { id: "why", label: "Why do we use cookies?" },
  { id: "control", label: "How can I control cookies?" },
  { id: "browser", label: "Control cookies on my browser" },
  { id: "beacons", label: "Other tracking technologies" },
  { id: "flash", label: "Flash cookies" },
  { id: "advertising", label: "Targeted advertising" },
  { id: "updates", label: "Updates to this policy" },
  { id: "info", label: "Further information" },
];

const CookiesPolicy = () => {
  return (
    <div className="cookies-page">
  

      <section className="cookies-hero">
        <div className="cookies-hero-inner">
          <span className="cookies-eyebrow">
            <Cookie size={15} /> Legal
          </span>
          <h1 className="cookies-title">
            Cookie <span>Policy</span>
          </h1>
          <p className="cookies-sub">
            This Cookie Policy explains how SpringUp Labs Private Limited uses cookies and similar technologies to recognize you when you visit our website.
          </p>
          <span className="cookies-updated">Last updated November 29, 2022</span>
        </div>
      </section>

      <div className="cookies-layout">
        <aside className="cookies-toc">
          <h4>On this page</h4>
          <ol>
            {sections.map((s) => (
              <li key={s.id}>
                <a href={`#${s.id}`}>{s.label}</a>
              </li>
            ))}
          </ol>
        </aside>

        <div className="cookies-content">
          <section className="cookies-section">
            <p>
              This Cookie Policy explains how SpringUp Labs Private Limited ("Company," "we," "us," and "our") uses cookies and similar technologies to recognize you when you visit our website at{" "}
              <a href="https://www.springuplabs.com" target="_blank" rel="noreferrer">https://www.springuplabs.com</a> &{" "}
              <a href="https://www.springuplabs.se" target="_blank" rel="noreferrer">https://www.springuplabs.se</a> ("Website"). It explains what these technologies are and why we use them, as well as your rights to control our use of them.
            </p>
          </section>

          <section className="cookies-section" id="what">
            <h2><span className="ico"><HelpCircle size={18} /></span>What are cookies?</h2>
            <p>
              Cookies are small data files that are placed on your computer or mobile device when you visit a website. Cookies are widely used by website owners in order to make their websites work or to work more efficiently, as well as to provide reporting information.
            </p>
          </section>

          <section className="cookies-section" id="why">
            <h2><span className="ico"><Cookie size={18} /></span>Why do we use cookies?</h2>
            <p>
              We use first- and third-party cookies for several reasons. Some cookies are required for technical reasons in order for our website to operate, and we refer to these as "essential" or "strictly necessary" cookies. Other cookies also enable us to track and target the interests of our users to enhance the experience on our Online Properties. Third parties serve cookies through our website for advertising, analytics, and other purposes. This is described in more detail below.
            </p>
          </section>

          <section className="cookies-section" id="control">
            <h2><span className="ico"><Settings size={18} /></span>How can I control cookies?</h2>
            <p>
              You have the right to decide whether to accept or reject cookies. You can exercise your cookie rights by setting your preferences in the Cookie Consent Manager. The Cookie Consent Manager allows you to select which categories of cookies you accept or reject. Essential cookies cannot be rejected as they are strictly necessary to provide you with services.
            </p>
            <p>
              The Cookie Consent Manager can be found in the notification banner and on our website. If you choose to reject cookies, you may still use our website though your access to some functionality and areas of our website may be restricted. You may also set or amend your web browser controls to accept or refuse cookies.
            </p>
            <p>
              The specific types of first- and third-party cookies served through our website and the purposes they perform are described in the table below (please note that the specific cookies served may vary depending on the specific Online Properties you visit).
            </p>
          </section>

          <section className="cookies-section" id="browser">
            <h2><span className="ico"><MonitorSmartphone size={18} /></span>How can I control cookies on my browser?</h2>
            <p>
              As the means by which you can refuse cookies through your web browser controls vary from browser to browser, you should visit your browser's help menu for more information. The following is information about how to manage cookies on the most popular browsers:
            </p>
            <ul>
              <li>Chrome</li>
              <li>Internet Explorer</li>
              <li>Firefox</li>
              <li>Safari</li>
              <li>Edge</li>
              <li>Opera</li>
            </ul>
            <p>
              In addition, most advertising networks offer you a way to opt-out of targeted advertising. If you would like to find out more information, please visit:
            </p>
            <ul>
              <li>Digital Advertising Alliance</li>
              <li>Digital Advertising Alliance of Canada</li>
              <li>European Interactive Digital Advertising Alliance</li>
            </ul>
          </section>

          <section className="cookies-section" id="beacons">
            <h2><span className="ico"><Info size={18} /></span>What about other tracking technologies, like web beacons?</h2>
            <p>
              Cookies are not the only way to recognize or track visitors to a website. We may use other, similar technologies from time to time, like web beacons (sometimes called "tracking pixels" or "clear gifs"). These are tiny graphics files that contain a unique identifier that enables us to recognize when someone has visited our website or opened an email including them. This allows us, for example, to monitor the traffic patterns of users from one page within a website to another, to deliver or communicate with cookies, to understand whether you have come to the website from an online advertisement displayed on a third-party website, to improve site performance, and to measure the success of email marketing campaigns. In many instances, these technologies are reliant on cookies to function properly, and so declining cookies will impair their functioning.
            </p>
          </section>

          <section className="cookies-section" id="flash">
            <h2><span className="ico"><Shield size={18} /></span>Do you use Flash cookies or Local Shared Objects?</h2>
            <p>
              Websites may also use so-called "Flash Cookies" (also known as Local Shared Objects or "LSOs") to, among other things, collect and store information about your use of our services, fraud prevention, and for other site operations.
            </p>
            <p>
              If you do not want Flash Cookies stored on your computer, you can adjust the settings of your Flash player to block Flash Cookies storage using the tools contained in the Website Storage Settings Panel. You can also control Flash Cookies by going to the Global Storage Settings Panel and following the instructions (which may include instructions that explain, for example, how to delete existing Flash Cookies (referred to as "information" on the Macromedia site), how to prevent Flash LSOs from being placed on your computer without your being asked, and (for Flash Player 8 and later) how to block Flash Cookies that are not being delivered by the operator of the page you are on at the time).
            </p>
            <p>
              Please note that setting the Flash Player to restrict or limit acceptance of Flash Cookies may reduce or impede the functionality of some Flash applications, including, potentially, Flash applications used in connection with our services or online content.
            </p>
          </section>

          <section className="cookies-section" id="advertising">
            <h2><span className="ico"><Target size={18} /></span>Do you serve targeted advertising?</h2>
            <p>
              Third parties may serve cookies on your computer or mobile device to serve advertising through our website. These companies may use information about your visits to this and other websites in order to provide relevant advertisements about goods and services that you may be interested in. They may also employ technology that is used to measure the effectiveness of advertisements. They can accomplish this by using cookies or web beacons to collect information about your visits to this and other sites in order to provide relevant advertisements about goods and services of potential interest to you. The information collected through this process does not enable us or them to identify your name, contact details, or other details that directly identify you unless you choose to provide these.
            </p>
          </section>

          <section className="cookies-section" id="updates">
            <h2><span className="ico"><RefreshCw size={18} /></span>How often will you update this Cookie Policy?</h2>
            <p>
              We may update this Cookie Policy from time to time in order to reflect, for example, changes to the cookies we use or for other operational, legal, or regulatory reasons. Please, therefore, revisit this Cookie Policy regularly to stay informed about our use of cookies and related technologies.
            </p>
            <p>The date at the top of this Cookie Policy indicates when it was last updated.</p>
          </section>

          <section className="cookies-section" id="info">
            <h2><span className="ico"><Info size={18} /></span>Where can I get further information?</h2>
            <p>
              If you have any questions about our use of cookies or other technologies, please email us at{" "}
              <a href="mailto:info@springuplabs.com">info@springuplabs.com</a> or by post to:
            </p>
            <div className="cookies-contact-card">
              <p><strong>SpringUp Labs Private Limited</strong></p>
              <p>Pune, Maharashtra</p>
              <p>India</p>
              <p>Email: <a href="mailto:info@springuplabs.com">info@springuplabs.com</a></p>
            </div>
          </section>
        </div>
      </div>

     
    </div>
  );
};

export default CookiesPolicy;